import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useDismiss } from './useDismiss';

export interface Suggestion {
  value: string;
  label: string;
  hint?: string | null;
}

/**
 * Поле с подсказкой из справочника `.field` + `.pop` (кит 2.11). Список
 * падает под поле на всю его ширину. Это поповер — **только вне окон**;
 * в модальном окне то же делает OptionList под полем.
 *
 * Свободный ввод без выбора из подсказки не сохраняется (БЛ M2 5.13):
 * пока ничего не выбрано, значение — null.
 */
export function Autocomplete({
  label,
  value,
  onChange,
  suggest,
  placeholder = 'начните вводить',
  error,
  hint,
}: {
  label: string;
  value: Suggestion | null;
  onChange: (value: Suggestion | null) => void;
  suggest: (query: string) => Promise<Suggestion[]>;
  placeholder?: string;
  error?: string | null;
  hint?: string;
}) {
  const [text, setText] = useState(value?.label ?? '');
  const [items, setItems] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId();
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, wrap);

  useEffect(() => setText(value?.label ?? ''), [value]);

  useEffect(() => {
    if (!open || text.trim().length < 2 || text === value?.label) return;
    let alive = true;
    const t = window.setTimeout(() => {
      suggest(text).then((r) => alive && setItems(r)).catch(() => alive && setItems([]));
    }, 200);
    return () => {
      alive = false;
      window.clearTimeout(t);
    };
  }, [text, open, suggest, value]);

  return (
    <div className={error ? 'field bad' : 'field'}>
      <label className="lbl" htmlFor={id}>
        {label}
      </label>
      <div className="wrapctl" ref={wrap}>
        <input
          id={id}
          value={text}
          placeholder={placeholder}
          onFocus={() => setOpen(true)}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
            if (value) onChange(null);
          }}
        />
        {open && text.trim().length >= 2 && text !== value?.label ? (
          <div className="pop">
            {items.length ? (
              items.map((s) => (
                <div
                  key={s.value}
                  className="op"
                  onClick={() => {
                    onChange(s);
                    setText(s.label);
                    setOpen(false);
                  }}
                >
                  {s.label}
                  {s.hint ? <span className="sb">{s.hint}</span> : null}
                </div>
              ))
            ) : (
              <div className="none">Ничего не нашлось</div>
            )}
          </div>
        ) : null}
      </div>
      {error ? <span className="err">{error}</span> : hint ? <span className="sub">{hint}</span> : null}
    </div>
  );
}
